<section class="campaign-details">
  @if (isLoading()) {
    <div class="campaign-details__state" aria-live="polite">
      <korviu-loader></korviu-loader>
    </div>
  } @else if (loadError()) {
    <div class="campaign-details__state" role="alert">
      <p>{{ 'promotions.campaignDetails.loadError' | translate }}</p>
      <button type="button" (click)="retry()">{{ 'promotions.retry' | translate }}</button>
    </div>
  } @else if (campaign(); as campaignData) {
    <header class="campaign-details__hero">
      <img
        class="campaign-details__header-decoration"
        src="assets/img/promotions/detail/header-decoration.png"
        alt=""
        aria-hidden="true"
      />
      <span class="campaign-details__badge campaign-details__badge--ghost" aria-hidden="true"></span>
      <div class="campaign-details__heading">
        <h1>{{ campaignData.name }}</h1>
        <div class="campaign-details__sponsor">
          <span>{{ 'promotions.detail.sponsoredBy' | translate }}</span>
          <img src="assets/img/promotions/detail/sponsor-avatar.png" alt="" aria-hidden="true" />
          <strong>UniGana</strong>
        </div>
      </div>
      @if (ganasBadgeValue(); as badgeValue) {
        <span class="campaign-details__badge" aria-hidden="true">
          <img class="campaign-details__badge-star" src="assets/img/promotions/detail/badge-star.svg" alt="" />
          <img class="campaign-details__badge-ellipse" src="assets/img/promotions/detail/badge-ellipse.svg" alt="" />
          <small>{{ 'promotions.campaignDetails.upTo' | translate }}</small>
          <strong>{{ badgeValue }}</strong>
          <span>Ganas</span>
        </span>
      } @else {
        <span class="campaign-details__badge campaign-details__badge--ghost" aria-hidden="true"></span>
      }
    </header>

    <div class="campaign-details__layout">
      <section class="campaign-details__entry-panel">
        <article
          class="campaign-details__state-banner"
          [class.campaign-details__state-banner--ended]="isEnded() && !isWinner()"
          [class.campaign-details__state-banner--winner]="isWinner()"
        >
          <h2>{{ stateBannerTitleKey() | translate }}</h2>
          <p>{{ stateBannerBodyKey() | translate }}</p>
        </article>

        <div class="campaign-details__tickets" aria-hidden="true">
          @for (slot of ticketSlots; track slot) {
            <img
              [src]="
                slot <= filledTicketCount()
                  ? 'assets/img/promotions/detail/ticket-diagonal-filled.svg'
                  : 'assets/img/promotions/detail/ticket-diagonal.svg'
              "
              alt=""
            />
          }
        </div>

        <div class="campaign-details__entry-summary">
          <strong>{{ 'promotions.detail.yourEntries' | translate }}</strong>
          <span>{{ entriesChanceKey() | translate: entriesChanceParams() }}</span>
        </div>

        @if (campaignData.objective_progress; as objectiveProgress) {
          <div class="campaign-details__objective" data-testid="campaign-details-objective">
            <div
              class="campaign-details__objective-bar"
              role="progressbar"
              aria-valuemin="0"
              aria-valuemax="100"
              [attr.aria-valuenow]="objectiveProgress.target ? objectiveProgress.current : 0"
            >
              <i [style.width.%]="objectiveProgress.target ? objectivePercent() : 0"></i>
            </div>
            <p>
              {{ 'promotions.missions.detail.progress' | translate }}:
              {{
                'promotions.missions.objectiveProgress'
                  | translate: { current: objectiveProgress.current, target: objectiveProgress.target }
              }}
            </p>
          </div>
        }

        <div class="campaign-details__stats">
          <article class="campaign-details__stat campaign-details__stat--cost">
            <span>{{ 'promotions.detail.cost' | translate }}</span>
            <strong>
              @if (costIsFree()) {
                {{ 'promotions.campaignDetails.free' | translate }}
              } @else {
                {{ entryCost() }}
              }
            </strong>
          </article>
          <article class="campaign-details__stat">
            <span>{{ 'promotions.detail.status' | translate }}</span>
            <strong
              class="campaign-details__tag"
              [class.campaign-details__tag--ended]="isEnded()"
              [class.campaign-details__tag--winner]="isWinner()"
            >
              {{ statusLabelKey() | translate }}
            </strong>
          </article>
          <article class="campaign-details__stat">
            <span>{{ 'promotions.detail.endDate' | translate }}</span>
            <strong>
              @if (campaignData.end_date) {
                {{ campaignData.end_date | date: 'dd MMM yy' }}
              } @else {
                {{ 'promotions.missions.noEndDate' | translate }}
              }
            </strong>
          </article>
        </div>

        @if (winnersIsLoading()) {
          <div class="campaign-details__winners-state" aria-live="polite">
            <korviu-loader></korviu-loader>
          </div>
        } @else if (winnersLoadError()) {
          <div class="campaign-details__winners-state" role="alert">
            <p>{{ 'promotions.campaignDetails.winnersLoadError' | translate }}</p>
            <button type="button" (click)="loadWinners(winnersCurrentPage())">
              {{ 'promotions.retry' | translate }}
            </button>
          </div>
        } @else if (topWinnerRows().length) {
          <section class="campaign-details__winners">
            <h2>{{ 'promotions.detail.winnersModal.title' | translate }}</h2>
            <div class="campaign-details__winners-list">
              @for (winner of topWinnerRows(); track winner.viewId) {
                <article>
                  <img class="campaign-details__medal" [src]="winner.medalUrl" alt="" aria-hidden="true" />
                  <img class="campaign-details__avatar" [src]="winner.avatarUrl" [alt]="winner.name" />
                  <strong>{{ winner.name }}</strong>
                  <span>
                    @if (winner.reward.value) {
                      {{ winner.reward.value }}
                    }
                    {{ winner.rewardLabelKey | translate }}
                  </span>
                </article>
              }
            </div>
            <button type="button" (click)="openWinnersModal()">
              {{ 'promotions.viewAll' | translate }}
            </button>
          </section>
        }
      </section>

      <section class="campaign-details__info-panel">
        <div class="campaign-details__cover">
          <img
            [src]="campaignData.image || 'assets/img/promotions/detail/promotion-cover.png'"
            [alt]="campaignData.image ? campaignData.name : ''"
          />
        </div>

        @if (campaignData.description) {
          <p class="campaign-details__description">{{ campaignData.description }}</p>
        }

        <div
          class="campaign-details__tabs"
          role="tablist"
          [attr.aria-label]="'promotions.detail.tabsLabel' | translate"
        >
          @for (tab of tabs; track tab.id) {
            <button
              type="button"
              role="tab"
              [attr.aria-selected]="selectedTab() === tab.id"
              [class.campaign-details__tab--active]="selectedTab() === tab.id"
              (click)="onTabClick(tab.id)"
            >
              {{ tab.labelKey | translate }}
            </button>
          }
        </div>

        <section class="campaign-details__table">
          @switch (selectedTab()) {
            @case ('entries') {
              <h2>{{ 'promotions.detail.entriesTab.title' | translate }}</h2>
              <dl>
                <dt>{{ 'promotions.detail.entriesTab.entriesPerUser' | translate }}</dt>
                <dd>
                  {{ campaignData.caps.max_entries_per_user ?? ('promotions.missions.unlimited' | translate) }}
                </dd>
              </dl>
              <dl>
                <dt>{{ 'promotions.detail.entriesTab.cost' | translate }}</dt>
                <dd>
                  @if (costIsFree()) {
                    {{ 'promotions.campaignDetails.free' | translate }}
                  } @else {
                    {{ entryCost() }}
                  }
                </dd>
              </dl>
              <dl>
                <dt>{{ 'promotions.detail.entriesTab.totalEntries' | translate }}</dt>
                <dd>{{ campaignData.entry_history.total_quantity }}</dd>
              </dl>
              <dl>
                <dt>{{ 'promotions.detail.entriesTab.entryLimit' | translate }}</dt>
                <dd>{{ campaignData.caps.total_entry_cap ?? ('promotions.missions.unlimited' | translate) }}</dd>
              </dl>
              <dl>
                <dt>{{ 'promotions.detail.entriesTab.howToEarnTickets' | translate }}</dt>
                <dd>{{ 'promotions.detail.entriesTab.howToEarnTicketsValue' | translate }}</dd>
              </dl>
            }
            @case ('conditions') {
              <h2>{{ 'promotions.detail.conditionsTab.title' | translate }}</h2>
              <dl>
                <dt>{{ 'promotions.campaignDetails.eligibility' | translate }}</dt>
                <dd>
                  {{ 'promotions.campaignDetails.audiences.' + campaignData.eligibility.audience | translate }}
                </dd>
              </dl>
              @for (condition of conditionRows(); track condition.viewId) {
                <dl>
                  <dt>{{ condition.actionLabelKey | translate }}</dt>
                  <dd>{{ 'promotions.campaignDetails.minimumCount' | translate: { count: condition.minCount } }}</dd>
                </dl>
              }
              <dl>
                <dt>{{ 'promotions.detail.conditionsTab.deadline' | translate }}</dt>
                <dd>
                  @if (campaignData.end_date) {
                    {{ campaignData.end_date | date: 'mediumDate' }}
                  } @else {
                    {{ 'promotions.missions.noEndDate' | translate }}
                  }
                </dd>
              </dl>
            }
            @default {
              <h2>{{ 'promotions.detail.promotionDetails' | translate }}</h2>
              <strong>{{ 'promotions.detail.id' | translate: { id: campaignData.id } }}</strong>
              <dl>
                <dt>{{ 'promotions.detail.winners' | translate }}</dt>
                <dd>{{ campaignData.winner_count }}</dd>
              </dl>
              <dl>
                <dt>{{ 'promotions.campaignDetails.eligibility' | translate }}</dt>
                <dd>
                  {{ 'promotions.campaignDetails.audiences.' + campaignData.eligibility.audience | translate }}
                </dd>
              </dl>
              <dl>
                <dt>{{ 'promotions.detail.duration' | translate }}</dt>
                <dd>
                  {{ campaignData.start_date | date: 'dd MMM yy' }}
                  –
                  @if (campaignData.end_date) {
                    {{ campaignData.end_date | date: 'dd MMM yy' }}
                  } @else {
                    {{ 'promotions.missions.noEndDate' | translate }}
                  }
                </dd>
              </dl>
              <dl>
                <dt>{{ 'promotions.detail.entryAvailability' | translate }}</dt>
                <dd>
                  @if (campaignData.caps.max_entries_per_user === null) {
                    {{ 'promotions.missions.unlimited' | translate }}
                  } @else if (costIsFree()) {
                    {{
                      'promotions.campaignDetails.entryAvailabilityValue'
                        | translate
                          : {
                              count: campaignData.caps.max_entries_per_user,
                              cost: ('promotions.campaignDetails.free' | translate)
                            }
                    }}
                  } @else {
                    {{
                      'promotions.campaignDetails.entryAvailabilityValue'
                        | translate: { count: campaignData.caps.max_entries_per_user, cost: entryCost() }
                    }}
                  }
                </dd>
              </dl>
            }
          }
        </section>
      </section>
    </div>
  } @else {
    <div class="campaign-details__state">
      <korviu-no-data size="medium" [message]="'promotions.campaignDetails.notFound' | translate"></korviu-no-data>
    </div>
  }
</section>
